import { useState } from 'react'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import { Button } from '@/components/ui/button'

type ViewMode = 'formatted' | 'source'

interface DocumentPreviewProps {
  content: string
  // Remove o frontmatter YAML do topo na visualização formatada (usado pela skill,
  // cujo Markdown é o SKILL.md publicado e começa com um bloco `--- ... ---`).
  stripFrontmatter?: boolean
  // Fundo do container: "document" (claro) para o processo, "skill" (mutado) para a skill.
  variant?: 'document' | 'skill'
  // Usado para escopar a seção em testes (cada documento tem seu próprio toggle).
  testId?: string
}

// Remove um único bloco de frontmatter YAML (`--- ... ---`) do início do Markdown.
// Sem isso, o react-markdown renderiza o primeiro `---` como uma régua solta e os
// campos `name:`/`description:` como um parágrafo cru na visualização formatada.
function withoutFrontmatter(markdown: string): string {
  return markdown.replace(/^\s*---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, '')
}

// Mostra um documento Markdown com alternância entre "Formatado" (renderizado) e
// "Código-fonte" (texto cru exato). O código-fonte sempre exibe o conteúdo
// completo, incluindo o frontmatter, para inspeção fiel do arquivo publicado.
export default function DocumentPreview({ content, stripFrontmatter = false, variant = 'document', testId }: DocumentPreviewProps) {
  const [mode, setMode] = useState<ViewMode>('formatted')
  const containerBg = variant === 'skill' ? 'bg-muted/40' : 'bg-background'
  const formattedContent = stripFrontmatter ? withoutFrontmatter(content) : content

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="inline-flex gap-1 rounded-md border p-0.5">
        <Button type="button" size="sm" variant={mode === 'formatted' ? 'secondary' : 'ghost'} onClick={() => setMode('formatted')}>
          Formatado
        </Button>
        <Button type="button" size="sm" variant={mode === 'source' ? 'secondary' : 'ghost'} onClick={() => setMode('source')}>
          Código-fonte
        </Button>
      </div>
      {mode === 'formatted' ? (
        // Documento renderizado: sem caixa/borda, flui na página como um documento.
        <SimpleMarkdown content={formattedContent} />
      ) : (
        // Código-fonte: bloco contido e rolável, com o texto cru exato.
        <pre className={`max-h-[28rem] overflow-auto rounded-md border ${containerBg} p-4 text-xs leading-relaxed text-foreground`}>
          <code>{content}</code>
        </pre>
      )}
    </div>
  )
}
